import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdvancedMarker, Map, useMap } from "@vis.gl/react-google-maps";
import { Dialog, DialogContent, IconButton, Paper, Tooltip } from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import MapIcon from "@mui/icons-material/Map";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import MDBox from "components/MDBox";
import RenderIcon from "examples/Maps/components/RenderIcon";
import TabletMapDetailsPanel, { toInteger } from "./TabletMapDetailsPanel";
import { CircleOverlay, RectangleOverlay, PolygonOverlay } from "../../../../geofence-management/index";

const GOOGLE_MAP_ID = "d089c1a7f8831a36";
const DEFAULT_ZOOM = 15;
const TWEEN_MS = 1000;
const MAP_TYPE_STORAGE_KEY = "tablet-map:type";

// Guard null/"" explicitly: Number(null) is 0 and would drop a pin at (0, 0).
const toPosition = (lat, lng) => {
  if (lat == null || lng == null || lat === "" || lng === "") return null;
  const next = { lat: Number(lat), lng: Number(lng) };
  return Number.isFinite(next.lat) && Number.isFinite(next.lng) ? next : null;
};

function formatTankLabel(levelPercent, volumeGallons) {
  const percent = toInteger(levelPercent);
  const volume = toInteger(volumeGallons);
  return [
    percent != null ? `${percent}%` : null,
    volume != null ? `${volume.toLocaleString()} gal` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function TankLabel({ text }) {
  if (!text) return null;
  return (
    <MDBox
      sx={{
        mb: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: "6px",
        backgroundColor: "rgba(15, 23, 42, 0.85)",
        color: "#fff",
        fontSize: "12px",
        fontWeight: 700,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
        boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
        pointerEvents: "none",
      }}
    >
      {text}
    </MDBox>
  );
}

function SitePin() {
  return (
    <MDBox
      sx={({ palette }) => ({
        color: palette.info.main,
        fontSize: "24px",
        lineHeight: 1,
        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
        pointerEvents: "none",
      })}
    >
      <i className="fa-solid fa-location-dot" />
    </MDBox>
  );
}

// Tweens the marker toward `target` so updates glide instead of jumping.
function useTweenedPosition(target) {
  const [position, setPosition] = useState(target);
  const currentRef = useRef(target);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!target) return undefined;
    const from = currentRef.current ?? target;
    if (from.lat === target.lat && from.lng === target.lng) return undefined;

    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / TWEEN_MS, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = {
        lat: from.lat + (target.lat - from.lat) * eased,
        lng: from.lng + (target.lng - from.lng) * eased,
      };
      currentRef.current = next;
      setPosition(next);
      if (t < 1) frameRef.current = requestAnimationFrame(step);
    };

    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target?.lat, target?.lng]);

  return position;
}

// Lives inside <Map> so useMap(mapId) resolves to that specific instance.
function FollowController({ mapId, target, follow, paused, onUserMove, recenterSignal }) {
  const map = useMap(mapId);

  useEffect(() => {
    if (!map) return undefined;
    const listeners = [
      map.addListener("dragstart", onUserMove),
      // Zoom is never set programmatically after mount, so any zoom change is the user.
      map.addListener("zoom_changed", onUserMove),
    ];
    return () => listeners.forEach((listener) => listener.remove());
  }, [map, onUserMove]);

  useEffect(() => {
    if (map && target && follow && !paused) map.panTo(target);
  }, [map, target?.lat, target?.lng, follow, paused]);

  useEffect(() => {
    if (map && target && recenterSignal) map.panTo(target);
  }, [recenterSignal]);

  return null;
}

function MapControls({ mapTypeId, onToggleType, follow, onRecenter, onExpand, onClose }) {
  const isSatellite = mapTypeId === "hybrid";
  return (
    <Paper
      elevation={0}
      sx={({ palette, functions: { rgba } }) => ({
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 1,
        display: "flex",
        gap: 0.5,
        p: 0.5,
        backgroundColor: rgba(palette.background.default, 0.85),
        border: `1px solid ${rgba(palette.text.main, 0.12)}`,
      })}
    >
      <Tooltip title={isSatellite ? "Map view" : "Satellite view"}>
        <IconButton size="small" onClick={onToggleType} aria-pressed={isSatellite}>
          {isSatellite ? <MapIcon fontSize="small" /> : <SatelliteAltIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title={follow ? "Following device" : "Recenter on device"}>
        <IconButton size="small" onClick={onRecenter} color={follow ? "info" : "default"}>
          <MyLocationIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {onExpand && (
        <Tooltip title="Expand map">
          <IconButton size="small" onClick={onExpand}>
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onClose && (
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
}

function TabletMapCanvas({
  mapId,
  target,
  markerPosition,
  sitePosition,
  siteName,
  initialCamera,
  icon,
  mapTypeId,
  follow,
  setFollow,
  onToggleType,
  onExpand,
  onClose,
  onCameraSnapshot,
  geofences = [],
}) {
  const [hovering, setHovering] = useState(false);
  const [recenterSignal, setRecenterSignal] = useState(0);

  const handleUserMove = useCallback(() => setFollow(false), [setFollow]);
  const handleRecenter = () => {
    setFollow(true);
    setRecenterSignal((value) => value + 1);
  };

  return (
    <MDBox
      position="relative"
      width="100%"
      height="100%"
      onPointerEnter={() => setHovering(true)}
      onPointerLeave={() => setHovering(false)}
    >
      <Map
        id={mapId}
        mapId={GOOGLE_MAP_ID}
        defaultCenter={initialCamera.center}
        defaultZoom={initialCamera.zoom}
        mapTypeId={mapTypeId}
        gestureHandling="greedy"
        disableDefaultUI
        clickableIcons={false}
        onCameraChanged={(event) => onCameraSnapshot?.(event.detail)}
        style={{ width: "100%", height: "100%" }}
      >
        {geofences.map((fence, index) => {
            const { latitude, longitude, color, geofence_type, radius, coordinates } = fence;
            const position = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
            
            if (geofence_type === "circle") {
                return <CircleOverlay key={index} center={position} radius={radius} options={{ fillColor: color, fillOpacity: 0.3, strokeColor: color, strokeWeight: 2 }} />;
            } else if (geofence_type === "rectangle" && coordinates?.length === 2) {
                return <RectangleOverlay key={index} bounds={{
                    north: Math.max(coordinates[0].lat, coordinates[1].lat),
                    south: Math.min(coordinates[0].lat, coordinates[1].lat),
                    east: Math.max(coordinates[0].lng, coordinates[1].lng),
                    west: Math.min(coordinates[0].lng, coordinates[1].lng),
                }} options={{ fillColor: color, fillOpacity: 0.3, strokeColor: color, strokeWeight: 2 }} />;
            } else if (geofence_type === "polygon" && coordinates) {
                return <PolygonOverlay key={index} paths={coordinates} options={{ fillColor: color, fillOpacity: 0.3, strokeColor: color, strokeWeight: 2 }} />;
            }
            return null;
        })}
        {sitePosition && (
          <AdvancedMarker position={sitePosition} title={siteName || "Assigned site"} zIndex={1}>
            <SitePin />
          </AdvancedMarker>
        )}
        {markerPosition && (
          <AdvancedMarker position={markerPosition} title="Device Location" zIndex={2}>
            {icon}
          </AdvancedMarker>
        )}
        <FollowController
          mapId={mapId}
          target={target}
          follow={follow}
          paused={hovering}
          onUserMove={handleUserMove}
          recenterSignal={recenterSignal}
        />
      </Map>
      <MapControls
        mapTypeId={mapTypeId}
        onToggleType={onToggleType}
        follow={follow}
        onRecenter={handleRecenter}
        onExpand={onExpand}
        onClose={onClose}
      />
    </MDBox>
  );
}

const TabletMap = ({
  lat,
  lng,
  moving = false,
  levelPercent,
  volumeGallons,
  options,
  details,
  site,
  geofences = [],
}) => {
  const target = useMemo(() => toPosition(lat, lng), [lat, lng]);
  const markerPosition = useTweenedPosition(target);
  const sitePosition = useMemo(() => toPosition(site?.lat, site?.lng), [site?.lat, site?.lng]);

  const [follow, setFollow] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [mapTypeId, setMapTypeId] = useState(() => {
    try {
      return localStorage.getItem(MAP_TYPE_STORAGE_KEY) || "roadmap";
    } catch (error) {
      return "roadmap";
    }
  });
  const cameraRef = useRef({ center: target, zoom: DEFAULT_ZOOM });

  const toggleMapType = () => {
    setMapTypeId((current) => {
      const next = current === "hybrid" ? "roadmap" : "hybrid";
      try {
        localStorage.setItem(MAP_TYPE_STORAGE_KEY, next);
      } catch (error) {
        console.error("persist map type", error);
      }
      return next;
    });
  };

  const snapshotCamera = useCallback((detail) => {
    cameraRef.current = { center: detail.center, zoom: detail.zoom };
  }, []);

  const tankLabel = formatTankLabel(levelPercent, volumeGallons);
  const heading = options?.heading ?? 0;

  const icon = useMemo(
    () => (
      <MDBox display="flex" flexDirection="column" alignItems="center">
        <TankLabel text={tankLabel} />
        <RenderIcon
          device={{
            color: options?.color || "#fff",
            // RenderIcon's built-in label is black and absolutely positioned; TankLabel replaces it.
            text: "",
            iconClassName: options?.icon || "fas fa-map-pin",
          }}
          animate={moving}
          heading={heading}
        />
      </MDBox>
    ),
    [tankLabel, options?.color, options?.icon, moving, heading]
  );

  if (!target) return null;

  const shared = {
    target,
    markerPosition,
    sitePosition,
    siteName: site?.name,
    icon,
    mapTypeId,
    follow,
    setFollow,
    onToggleType: toggleMapType,
  };

  return (
    <>
      <TabletMapCanvas
        {...shared}
        geofences={geofences}
        mapId="tablet-detail-inline"
        initialCamera={{ center: target, zoom: DEFAULT_ZOOM }}
        onExpand={() => setExpanded(true)}
        onCameraSnapshot={snapshotCamera}
      />

      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { height: "85vh" } }}
      >
        <DialogContent
          sx={{
            p: 0,
            height: "100%",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            overflowY: { xs: "auto", md: "hidden" },
          }}
        >
          {expanded && (
            <>
              <MDBox flex={1} minHeight={{ xs: "55vh", md: 0 }} position="relative">
                <TabletMapCanvas
                  {...shared}
                  geofences={geofences}
                  mapId="tablet-detail-expanded"
                  initialCamera={cameraRef.current}
                  onClose={() => setExpanded(false)}
                />
              </MDBox>
              <TabletMapDetailsPanel
                details={details}
                site={site}
                sitePosition={sitePosition}
                position={target}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TabletMap;
